/**
 * Point Symbol formatting for a north arrow, following ArcGIS Pro's Format
 * Element > Point Symbol pane: Appearance and Halo sections, a live preview,
 * and Auto Apply / Apply / Cancel commit control.
 */

import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Icon, tokens } from '@/design';
import { LayoutNorthArrow, NORTH_ARROW_STYLES } from './LayoutNorthArrow';
import { ShapeMarkerLayerPanel } from './ShapeMarkerLayerPanel';
import { SymbolColorPicker } from './SymbolColorPicker';
import { BLACK, WHITE } from './symbolColors';
import type {
  NorthArrowHalo,
  NorthArrowStyleId,
  NorthArrowSymbol,
  SymbolAngleAlignment,
} from './layoutTypes';

export interface PointSymbolDraft {
  readonly styleId: NorthArrowStyleId;
  readonly symbol: NorthArrowSymbol;
  readonly sizePt: number;
}

export interface PointSymbolPanelProps {
  readonly styleId: NorthArrowStyleId;
  readonly symbol: NorthArrowSymbol;
  readonly sizePt: number;
  /** Element rotation without the symbol angle, so the preview stays live. */
  readonly baseRotationDeg: number;
  readonly onApply: (next: PointSymbolDraft) => void;
}

type SymbolTab = 'appearance' | 'layers' | 'tools';

const PREVIEW_ZOOMS = [50, 75, 100, 150, 200, 400] as const;
const SIZE_PRESETS_PT = [36, 72, 144] as const;
const CHECKER = `repeating-conic-gradient(${tokens.color.surface[3]} 0% 25%, ${tokens.color.surface[1]} 0% 50%) 0 0 / ${tokens.space[3]}px ${tokens.space[3]}px`;

const DEFAULT_SYMBOL: NorthArrowSymbol = {
  color: BLACK,
  angle: 0,
  angleAlignment: 'map',
  marker: {
    visible: true,
    source: 'style',
    formId: 'circle',
    fontGlyph: 'N',
    fileDataUrl: null,
    fileName: null,
    fillColor: BLACK,
    outlineColor: BLACK,
    outlineWidthPt: 0,
    respectFrame: true,
    scaleProportionally: true,
  },
  halo: { enabled: false, color: WHITE, outlineColor: BLACK, sizePt: 1.5 },
};

function draftKey(draft: PointSymbolDraft): string {
  return JSON.stringify({
    styleId: draft.styleId,
    symbol: draft.symbol,
    sizePt: Number(draft.sizePt.toFixed(3)),
  });
}

export function PointSymbolPanel({
  styleId,
  symbol,
  sizePt,
  baseRotationDeg,
  onApply,
}: PointSymbolPanelProps) {
  const committed = useMemo<PointSymbolDraft>(
    () => ({ styleId, symbol, sizePt }),
    [styleId, symbol, sizePt],
  );
  const [draft, setDraft] = useState<PointSymbolDraft>(committed);
  const [autoApply, setAutoApply] = useState(false);
  const [tab, setTab] = useState<SymbolTab>('appearance');
  const [previewZoom, setPreviewZoom] = useState<number>(100);
  const [transparentPreview, setTransparentPreview] = useState(false);

  // Re-sync only when the element itself changes (our own commits included).
  useEffect(() => {
    setDraft(committed);
  }, [committed]);

  const dirty = draftKey(draft) !== draftKey(committed);

  const commit = (next: PointSymbolDraft) => {
    setDraft(next);
    if (autoApply) onApply(next);
  };
  const editSymbol = (patch: Partial<NorthArrowSymbol>) =>
    commit({ ...draft, symbol: { ...draft.symbol, ...patch } });
  const editHalo = (patch: Partial<NorthArrowHalo>) =>
    editSymbol({ halo: { ...draft.symbol.halo, ...patch } });
  const editMarker = (patch: Partial<NorthArrowSymbol['marker']>) => {
    const marker = { ...draft.symbol.marker, ...patch };
    commit({
      ...draft,
      symbol: {
        ...draft.symbol,
        marker,
        ...(patch.fillColor !== undefined ? { color: patch.fillColor } : {}),
      },
    });
  };

  const field: CSSProperties = {
    minHeight: tokens.density.rowDense,
    width: '100%',
    boxSizing: 'border-box',
    padding: `0 ${tokens.space[2]}px`,
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: tokens.color.surface[0],
    color: tokens.color.ink.primary,
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  };
  const row: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `minmax(0, 1fr) ${tokens.layout.popoverXs / 2}px`,
    alignItems: 'center',
    gap: tokens.space[2],
    fontSize: tokens.font.size.sm,
  };
  const sectionHeading: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.space[1],
    margin: `${tokens.space[3]}px 0 ${tokens.space[2]}px`,
    fontSize: tokens.font.size.sm,
    fontWeight: tokens.font.weight.semibold,
  };
  const tabButton = (active: boolean): CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: tokens.density.rowDense,
    minHeight: tokens.density.rowDense,
    border: 0,
    borderBottom: `${tokens.focus.ringWidth}px solid ${
      active ? tokens.color.semantic.info : 'transparent'
    }`,
    background: 'transparent',
    color: active ? tokens.color.semantic.info : tokens.color.ink.secondary,
    cursor: 'pointer',
  });
  const actionButton = (enabled: boolean): CSSProperties => ({
    minHeight: tokens.density.rowDense,
    padding: `0 ${tokens.space[3]}px`,
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: tokens.color.surface[0],
    color: enabled ? tokens.color.ink.primary : tokens.color.ink.muted,
    cursor: enabled ? 'pointer' : 'not-allowed',
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  });

  const previewRotation = baseRotationDeg + draft.symbol.angle;

  return (
    <div data-testid="point-symbol-panel">
      <label style={{ display: 'grid', gap: tokens.space[1] }}>
        <span
          style={{
            color: tokens.color.semantic.info,
            fontSize: tokens.font.size.md,
            borderBottom: `${tokens.focus.ringWidth}px solid ${tokens.color.semantic.info}`,
            paddingBottom: tokens.space[1],
          }}
        >
          Point Symbol
        </span>
        <select
          aria-label="Point symbol style"
          value={draft.styleId}
          onChange={(event) =>
            commit({ ...draft, styleId: event.target.value as NorthArrowStyleId })
          }
          style={field}
        >
          {NORTH_ARROW_STYLES.map((style) => (
            <option key={style.id} value={style.id}>{style.label}</option>
          ))}
        </select>
      </label>

      <div
        role="tablist"
        aria-label="Point symbol sections"
        style={{
          display: 'flex',
          gap: tokens.space[2],
          marginTop: tokens.space[3],
          borderBottom: tokens.border.thin,
        }}
      >
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'appearance'}
          aria-label="Format symbol"
          onClick={() => setTab('appearance')}
          style={tabButton(tab === 'appearance')}
        >
          <Icon name="palette" size={20} decorative />
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'layers'}
          aria-label="Symbol layers"
          onClick={() => setTab('layers')}
          style={tabButton(tab === 'layers')}
        >
          <Icon name="layers" size={20} decorative />
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'tools'}
          aria-label="Symbol tools"
          onClick={() => setTab('tools')}
          style={tabButton(tab === 'tools')}
        >
          <Icon name="tune" size={20} decorative />
        </button>
      </div>

      {tab === 'appearance' ? (
        <div role="tabpanel" aria-label="Format symbol">
          <h4 style={sectionHeading}>
            <Icon name="expand_more" size={16} decorative /> Appearance
          </h4>
          <div style={{ display: 'grid', gap: tokens.space[2] }}>
            <div style={row}>
              <span>Color</span>
              <SymbolColorPicker
                label="Symbol color"
                value={draft.symbol.marker.fillColor}
                onChange={(fillColor) => editMarker({ fillColor })}
              />
            </div>
            <label style={row}>
              <span>Size</span>
              <input
                aria-label="Symbol size in points"
                type="number"
                min={4}
                max={1200}
                step={1}
                value={Number(draft.sizePt.toFixed(2))}
                onChange={(event) => {
                  const next = Number(event.target.value);
                  if (Number.isFinite(next) && next > 0) {
                    commit({ ...draft, sizePt: next });
                  }
                }}
                style={field}
              />
            </label>
            <label style={row}>
              <span>Angle</span>
              <input
                aria-label="Symbol angle"
                type="number"
                min={0}
                max={360}
                step={1}
                value={Number(draft.symbol.angle.toFixed(2))}
                onChange={(event) => {
                  const next = Number(event.target.value);
                  if (Number.isFinite(next)) {
                    editSymbol({ angle: ((next % 360) + 360) % 360 });
                  }
                }}
                style={field}
              />
            </label>
            <label style={row}>
              <span>Angle alignment</span>
              <select
                aria-label="Symbol angle alignment"
                value={draft.symbol.angleAlignment}
                onChange={(event) =>
                  editSymbol({
                    angleAlignment: event.target.value as SymbolAngleAlignment,
                  })
                }
                style={field}
              >
                <option value="display">Display</option>
                <option value="map">Map</option>
              </select>
            </label>
          </div>

          <h4 style={sectionHeading}>
            <Icon name="expand_more" size={16} decorative /> Halo
          </h4>
          <div style={{ display: 'grid', gap: tokens.space[2] }}>
            <label style={row}>
              <span>Halo symbol</span>
              <select
                aria-label="Halo symbol"
                value={draft.symbol.halo.enabled ? 'solid' : 'none'}
                onChange={(event) =>
                  editHalo({ enabled: event.target.value === 'solid' })
                }
                style={field}
              >
                <option value="none">No halo</option>
                <option value="solid">Solid fill</option>
              </select>
            </label>
            <div style={{ ...row, opacity: draft.symbol.halo.enabled ? 1 : 0.5 }}>
              <span>Color</span>
              <SymbolColorPicker
                label="Halo color"
                value={draft.symbol.halo.color}
                allowNoColor={false}
                disabled={!draft.symbol.halo.enabled}
                onChange={(color) => editHalo({ color: color ?? WHITE })}
              />
            </div>
            <div style={{ ...row, opacity: draft.symbol.halo.enabled ? 1 : 0.5 }}>
              <span>Outline color</span>
              <SymbolColorPicker
                label="Halo outline color"
                value={draft.symbol.halo.outlineColor}
                allowNoColor={false}
                disabled={!draft.symbol.halo.enabled}
                onChange={(color) => editHalo({ outlineColor: color ?? BLACK })}
              />
            </div>
            <label style={{ ...row, opacity: draft.symbol.halo.enabled ? 1 : 0.5 }}>
              <span>Halo size</span>
              <input
                aria-label="Halo size"
                type="number"
                min={0}
                max={12}
                step={0.5}
                disabled={!draft.symbol.halo.enabled}
                value={draft.symbol.halo.sizePt}
                onChange={(event) => {
                  const next = Number(event.target.value);
                  if (Number.isFinite(next) && next >= 0) {
                    editHalo({ sizePt: next });
                  }
                }}
                style={field}
              />
            </label>
          </div>
        </div>
      ) : null}

      {tab === 'layers' ? (
        <div
          role="tabpanel"
          aria-label="Symbol layers"
          style={{ marginTop: tokens.space[3] }}
        >
          <ShapeMarkerLayerPanel
            marker={draft.symbol.marker}
            styleId={draft.styleId}
            sizePt={draft.sizePt}
            onMarkerChange={editMarker}
            onStyleChange={(nextStyleId) =>
              commit({
                ...draft,
                styleId: nextStyleId,
                symbol: {
                  ...draft.symbol,
                  marker: { ...draft.symbol.marker, source: 'style' },
                },
              })
            }
            onSizeChange={(nextSizePt) =>
              commit({ ...draft, sizePt: nextSizePt })
            }
          />
        </div>
      ) : null}

      {tab === 'tools' ? (
        <div
          role="tabpanel"
          aria-label="Symbol tools"
          style={{ display: 'grid', gap: tokens.space[2], marginTop: tokens.space[3] }}
        >
          <button
            type="button"
            onClick={() => commit({ ...draft, symbol: DEFAULT_SYMBOL })}
            style={actionButton(true)}
          >
            Reset symbol to default
          </button>
          <button
            type="button"
            onClick={() =>
              editSymbol({ angle: Math.round(draft.symbol.angle / 45) * 45 % 360 })
            }
            style={actionButton(true)}
          >
            Round angle to nearest 45°
          </button>
          <div style={{ display: 'flex', gap: tokens.space[2] }}>
            {SIZE_PRESETS_PT.map((preset) => (
              <button
                key={preset}
                type="button"
                aria-label={`Set symbol size to ${preset} points`}
                onClick={() => commit({ ...draft, sizePt: preset })}
                style={actionButton(true)}
              >
                {preset} pt
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <hr
        aria-hidden
        style={{
          margin: `${tokens.space[3]}px 0`,
          border: 0,
          borderTop: `${tokens.border.width.thin}px dashed ${tokens.color.surface[3]}`,
        }}
      />

      <div
        data-testid="point-symbol-preview"
        aria-label="Point symbol preview"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: tokens.layout.rightPanel / 2,
          overflow: 'hidden',
          border: tokens.border.thin,
          background: transparentPreview ? CHECKER : tokens.color.canvas.light,
        }}
      >
        <span
          style={{
            display: 'block',
            width: `${previewZoom * 0.64}%`,
            height: `${previewZoom * 0.8}%`,
          }}
        >
          <LayoutNorthArrow
            styleId={draft.styleId}
            bearingDeg={0}
            rotationDeg={previewRotation}
            color={draft.symbol.color}
            marker={draft.symbol.marker}
            halo={draft.symbol.halo}
          />
        </span>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: tokens.space[2],
          marginTop: tokens.space[2],
        }}
      >
        <select
          aria-label="Preview zoom"
          value={previewZoom}
          onChange={(event) => setPreviewZoom(Number(event.target.value))}
          style={{ ...field, width: 'auto' }}
        >
          {PREVIEW_ZOOMS.map((zoom) => (
            <option key={zoom} value={zoom}>{zoom}%</option>
          ))}
        </select>
        <button
          type="button"
          aria-label="Toggle transparent preview background"
          aria-pressed={transparentPreview}
          onClick={() => setTransparentPreview((previous) => !previous)}
          style={{
            ...actionButton(true),
            padding: `0 ${tokens.space[2]}px`,
            background: transparentPreview
              ? tokens.color.accent.subtle
              : tokens.color.surface[0],
          }}
        >
          <Icon name="texture" size={16} decorative />
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: tokens.space[2],
          marginTop: tokens.space[3],
          paddingTop: tokens.space[2],
          borderTop: tokens.border.thin,
        }}
      >
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: tokens.space[2],
            fontSize: tokens.font.size.sm,
          }}
        >
          <input
            type="checkbox"
            role="switch"
            aria-label="Auto apply symbol changes"
            checked={autoApply}
            onChange={(event) => {
              setAutoApply(event.target.checked);
              if (event.target.checked && dirty) onApply(draft);
            }}
          />
          Auto Apply
        </label>
        <div style={{ display: 'flex', gap: tokens.space[2] }}>
          <button
            type="button"
            aria-label="Apply symbol changes"
            disabled={!dirty}
            onClick={() => onApply(draft)}
            style={actionButton(dirty)}
          >
            Apply
          </button>
          <button
            type="button"
            aria-label="Cancel symbol changes"
            disabled={!dirty}
            onClick={() => setDraft(committed)}
            style={actionButton(dirty)}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
